import { z } from 'zod';
import { ENVIRONMENT_TYPES } from '@terra/api-common-constants';

export const FindingAuditActionSchema = z.enum([
  'CREATED',
  'STATUS_CHANGED',
  'SEVERITY_CHANGED',
  'SEVERITY_CHANGE_REQUESTED',
  'SEVERITY_CHANGE_REJECTED',
  'CVSS_SCORE_CHANGED',
  'CVSS_VECTOR_CHANGED',
  'OWASP_RISK_LEVEL_CHANGED',
  'ASSIGNED',
  'EXTERNAL_REFERENCE_ADDED',
  'EXTERNAL_REFERENCE_REMOVED',
  'EXTERNAL_REFERENCE_UPDATED',
  'PUBLISHED',
  'UNPUBLISHED',
  'REMEDIATION_PLAN_CREATED',
  'REMEDIATION_PR_CREATED',
  'REMEDIATION_PR_LINKED',
  'REMEDIATION_PR_MERGED',
  'RETEST_SCHEDULED',
  'RETEST_STARTED',
  'RETEST_COMPLETED',
  'RETEST_ABORTED',
  'PREVENTION_CONTROL_STAGED',
  'PREVENTION_CONTROL_ACTIVATED',
  'PREVENTION_CONTROL_REJECTED',
  'PREVENTION_CONTROL_REMOVED',
  'PREVENTION_CONTROL_DRIFTED',
  'ARCHIVED',
  'RESTORED',
]);

export const AuditActorTypeSchema = z.enum([
  'USER',
  'SYSTEM',
  'INTEGRATION',
  'AI',
]);

export const FINDING_AUDIT_AGENT_ACTOR = {
  NEO: 'Neo Agent',
  REMEDIATION: 'Remediation Agent',
  PREVENTION: 'Prevention Agent',
  SCHEDULER: 'Scheduler Agent',
  SYSTEM: 'System',
} as const;

export const FINDING_AUDIT_TERRA_HITL_DISPLAY_NAME =
  'Terra Human on the Loop' as const;

export const FINDING_AUDIT_RETEST_FAILED_CUSTOMER_NOTE =
  'Retest failed, please run another retest.' as const;

export type FindingAuditAgentActor =
  (typeof FINDING_AUDIT_AGENT_ACTOR)[keyof typeof FINDING_AUDIT_AGENT_ACTOR];

export const FindingAuditRequestedBySchema = z.object({
  actorType: AuditActorTypeSchema,
  actorId: z.string().optional(),
  actorName: z.string().optional(),
  isTerraUser: z.boolean().optional(),
});

export type FindingAuditRequestedBy = z.infer<
  typeof FindingAuditRequestedBySchema
>;

export const parseFindingAuditRequestedBy = (
  raw: unknown,
): FindingAuditRequestedBy | undefined => {
  const parsed = FindingAuditRequestedBySchema.safeParse(raw);

  return parsed.success ? parsed.data : undefined;
};

export const FindingAuditLogSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  isTerraUser: z.boolean(),
  findingId: z.string(),
  action: FindingAuditActionSchema,
  fieldName: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  actorType: AuditActorTypeSchema,
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  note: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
});

export const FindingAuditLogsResponseSchema = z.object({
  logs: z.array(FindingAuditLogSchema),
  total: z.number(),
});

export const MTTRDataSchema = z.object({
  findingId: z.string(),
  publishedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  mttrHours: z.number().nullable(),
  mttrDays: z.number().nullable(),
});

export const AggregateMTTRQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  applicationId: z.string().optional(),
  targetId: z.string().optional(),
  severity: z
    .array(z.enum(['Critical', 'High', 'Medium', 'Low', 'Info']))
    .optional(),
  businessUnitIds: z.array(z.string()).optional(),
  environments: z.array(z.enum(ENVIRONMENT_TYPES)).optional(),
});

export const AggregateMTTRDataSchema = z.object({
  averageMttrDays: z.number().nullable(),
  medianMttrDays: z.number().nullable(),
  totalFindings: z.number(),
  resolvedFindings: z.number(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

export const FindingsOverTimeQuerySchema = z.object({
  days: z.number().optional(),
  severity: z
    .array(z.enum(['Critical', 'High', 'Medium', 'Low', 'Info']))
    .optional(),
  applicationId: z.string().optional(),
  targetId: z.string().optional(),
});

export const FindingsOverTimeDataSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const FindingsOverTimeResponseSchema = z.object({
  data: z.array(FindingsOverTimeDataSchema),
  total: z.number(),
});

export const FindingsByStatusOverTimeQuerySchema = z.object({
  days: z.number().optional(),
  applicationId: z.string().optional(),
  targetId: z.string().optional(),
});

export const FindingsByStatusOverTimeDataSchema = z.object({
  date: z.string(),
  unresolved: z.number(),
  resolved: z.number(),
});

export const FindingsByStatusOverTimeResponseSchema = z.object({
  data: z.array(FindingsByStatusOverTimeDataSchema),
});

// Dashboard-level trend query schema with full filtering options
export const DashboardTrendQuerySchema = z.object({
  from: z
    .string()
    .optional()
    .describe('Start date for the trend period (ISO 8601 format)'),
  to: z
    .string()
    .optional()
    .describe('End date for the trend period (ISO 8601 format)'),
  environment: z
    .enum(ENVIRONMENT_TYPES)
    .optional()
    .describe('Filter by application environment'),
  businessUnitId: z.string().optional().describe('Filter by business unit ID'),
  applicationId: z.string().optional().describe('Filter by application ID'),
  targetId: z.string().optional().describe('Alias for application ID'),
});

// MTTR Over Time - returns MTTR values per day broken down by severity
export const MTTROverTimeDataSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  critical: z.number().describe('Average MTTR in days for critical findings'),
  high: z.number().describe('Average MTTR in days for high findings'),
  medium: z.number().describe('Average MTTR in days for medium findings'),
  low: z.number().describe('Average MTTR in days for low findings'),
  info: z.number().describe('Average MTTR in days for info findings'),
});

export const MTTROverTimeResponseSchema = z.object({
  data: z.array(MTTROverTimeDataSchema),
});

// Resolved vs Unresolved Findings Over Time
export const ResolvedUnresolvedTrendDataSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  resolved: z
    .number()
    .describe('Count of resolved findings (cumulative or daily)'),
  unresolved: z
    .number()
    .describe('Count of unresolved findings (cumulative or daily)'),
});

export const ResolvedUnresolvedTrendResponseSchema = z.object({
  data: z.array(ResolvedUnresolvedTrendDataSchema),
});

export type FindingAuditAction = z.infer<typeof FindingAuditActionSchema>;
export type AuditActorType = z.infer<typeof AuditActorTypeSchema>;
export type FindingAuditLog = z.infer<typeof FindingAuditLogSchema>;
export type FindingAuditLogsResponse = z.infer<
  typeof FindingAuditLogsResponseSchema
>;
export type MTTRData = z.infer<typeof MTTRDataSchema>;
export type AggregateMTTRQuery = z.infer<typeof AggregateMTTRQuerySchema>;
export type AggregateMTTRData = z.infer<typeof AggregateMTTRDataSchema>;
export type FindingsOverTimeQuery = z.infer<typeof FindingsOverTimeQuerySchema>;
export type FindingsOverTimeData = z.infer<typeof FindingsOverTimeDataSchema>;
export type FindingsOverTimeResponse = z.infer<
  typeof FindingsOverTimeResponseSchema
>;
export type FindingsByStatusOverTimeQuery = z.infer<
  typeof FindingsByStatusOverTimeQuerySchema
>;
export type FindingsByStatusOverTimeData = z.infer<
  typeof FindingsByStatusOverTimeDataSchema
>;
export type FindingsByStatusOverTimeResponse = z.infer<
  typeof FindingsByStatusOverTimeResponseSchema
>;
export type DashboardTrendQuery = z.infer<typeof DashboardTrendQuerySchema>;
export type MTTROverTimeData = z.infer<typeof MTTROverTimeDataSchema>;
export type MTTROverTimeResponse = z.infer<typeof MTTROverTimeResponseSchema>;
export type ResolvedUnresolvedTrendData = z.infer<
  typeof ResolvedUnresolvedTrendDataSchema
>;
export type ResolvedUnresolvedTrendResponse = z.infer<
  typeof ResolvedUnresolvedTrendResponseSchema
>;
